import type { Questionnaire, QuestionnaireItem } from "fhir/r4";
import { EXT } from "./index";
const choice = (
  id: string,
  text: string,
  labels: string[],
): QuestionnaireItem => ({
  linkId: id,
  text,
  type: "choice",
  required: true,
  answerOption: labels.map((valueString) => ({ valueString })),
});
export const templates: {
  id: string;
  category: string;
  color: string;
  title: string;
  subtitle: string;
  questionnaire: Questionnaire;
}[] = [
  {
    id: "wellbeing",
    category: "Patient-reported outcomes",
    color: "violet",
    title: "Wellbeing check-in",
    subtitle: "A short conversation about how your patient is feeling.",
    questionnaire: {
      resourceType: "Questionnaire",
      status: "draft",
      title: "Wellbeing check-in",
      description:
        "Tell us how you have been feeling over the last two weeks. There are no right or wrong answers.",
      language: "en",
      subjectType: ["Patient"],
      item: [
        {
          linkId: "wellbeing",
          type: "group",
          text: "Your wellbeing",
          item: [
            choice("energy", "How would you describe your energy levels?", [
              "Very low",
              "Low",
              "Moderate",
              "High",
              "Very high",
            ]),
            choice("sleep", "How well have you been sleeping?", [
              "Very poorly",
              "Poorly",
              "Fairly well",
              "Well",
              "Very well",
            ]),
            {
              linkId: "pain",
              type: "integer",
              text: "How much pain are you experiencing today?",
              required: true,
              extension: [
                { url: EXT.min, valueInteger: 0 },
                { url: EXT.max, valueInteger: 10 },
                {
                  url: EXT.help,
                  valueString:
                    "0 means no pain. 10 means the worst pain you can imagine.",
                },
              ],
            },
            {
              linkId: "support",
              type: "boolean",
              text: "Would you like to discuss anything with your care team?",
              required: true,
            },
            {
              linkId: "concerns",
              type: "text",
              text: "What would you like to talk about?",
              enableWhen: [
                { question: "support", operator: "=", answerBoolean: true },
              ],
              extension: [
                {
                  url: EXT.help,
                  valueString:
                    "Share as much or as little as you feel comfortable with.",
                },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: "intake",
    category: "Clinical intake",
    color: "orange",
    title: "Before your visit",
    subtitle:
      "Understand symptoms, medications and the reason for today’s visit.",
    questionnaire: {
      resourceType: "Questionnaire",
      status: "draft",
      title: "Before your visit",
      description: "Help your care team prepare for your appointment.",
      language: "en",
      subjectType: ["Patient"],
      item: [
        {
          linkId: "visit",
          type: "group",
          text: "Today’s visit",
          item: [
            {
              linkId: "reason",
              type: "text",
              text: "What brings you in today?",
              required: true,
            },
            {
              linkId: "onset",
              type: "date",
              text: "When did your symptoms start?",
            },
            choice(
              "severity",
              "How much do these symptoms affect your daily life?",
              ["Not at all", "A little", "Moderately", "A lot"],
            ),
          ],
        },
        {
          linkId: "medication",
          type: "group",
          text: "Your medications",
          repeats: true,
          item: [
            {
              linkId: "medicine",
              type: "string",
              text: "Medication name",
              required: true,
            },
            {
              linkId: "dose",
              type: "string",
              text: "Dose and how often you take it",
            },
          ],
        },
      ],
    },
  },
  {
    id: "feedback",
    category: "Patient experience",
    color: "green",
    title: "Your visit, your voice",
    subtitle: "Give patients space to share their experience at your clinic.",
    questionnaire: {
      resourceType: "Questionnaire",
      status: "draft",
      title: "Your visit, your voice",
      description: "Your feedback helps us provide better care.",
      language: "en",
      item: [
        choice("listened", "Did you feel listened to during your visit?", [
          "Not at all",
          "Somewhat",
          "Mostly",
          "Completely",
        ]),
        choice("clear", "Were the next steps in your care explained clearly?", [
          "Not at all",
          "Somewhat",
          "Mostly",
          "Completely",
        ]),
        {
          linkId: "feedback",
          type: "text",
          text: "Is there anything we could do better?",
        },
      ],
    },
  },
];
